import React from "react";
import "@/App.css";
// Register all built-in map providers ONCE at app boot. Adding a new provider
// (MapLibre / Mappls / Google) later is a one-line change inside components/map.
import "@/components/map";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { useGlobalPush } from "@/hooks/useGlobalPush";
import { GlobalRideCallBanner } from "@/components/ride/GlobalRideCallBanner";
import { StepUpDialog } from "@/components/StepUpDialog";
import { LocationProvider } from "@/context/LocationContext";
import { I18nProvider } from "@/context/I18nContext";
import Layout from "@/components/Layout";
import { OfflineBanner } from "@/components/OfflineBanner";
import { SyncManager } from "@/lib/syncManager";
import Home from "@/pages/Home";
import NotFound from "@/pages/NotFound";
import Ott from "@/pages/Ott";
import OttWatch from "@/pages/OttWatch";
import OttUpload from "@/pages/OttUpload";
import OttCreator from "@/pages/OttCreator";
import Marketplace from "@/pages/Marketplace";
import AdminMarketplace from "@/pages/AdminMarketplace";
import AdminPlaces from "@/pages/AdminPlaces";
import MyTools from "@/pages/MyTools";
import Calculator from "@/pages/Calculator";
import HrDashboard from "@/pages/HrDashboard";
import Search from "@/pages/Search";
import Venues from "@/pages/Venues";
import Venue from "@/pages/Venue";
import VenueOwner from "@/pages/VenueOwner";
import AdminVenueEditor from "@/pages/AdminVenueEditor";
import Superapp from "@/pages/Superapp";
import RideTrack from "@/pages/RideTrack";
import RideSplit from "@/pages/RideSplit";
import ProviderShift from "@/pages/ProviderShift";
import LanguageSettings from "@/pages/LanguageSettings";
import EmergencyContacts from "@/pages/EmergencyContacts";
import CallId from "@/pages/CallId";
import CallResolve from "@/pages/CallResolve";
import DevicePair from "@/pages/DevicePair";
import LearnEarn from "@/pages/LearnEarn";
import PlacesLive from "@/pages/PlacesLive";
import LocationBootstrap from "@/components/LocationBootstrap";
import LanguageAutoDetect from "@/components/LanguageAutoDetect";
import NativeVoicePrompt from "@/components/NativeVoicePrompt";
import ListingDetail from "@/pages/ListingDetail";
import TrackBooking from "@/pages/TrackBooking";
import PostListing from "@/pages/PostListing";
import Dashboard from "@/pages/Dashboard";
import Account from "@/pages/Account";
import Citizens from "@/pages/Citizens";
import Onboarding from "@/pages/Onboarding";
import Emergency from "@/pages/Emergency";
import LiftSOS from "@/pages/LiftSOS";
import AmbulancePage from "@/pages/Ambulance";
import AdminSos from "@/pages/AdminSos";
import AdminAi from "@/pages/AdminAi";
import AdminVoice from "@/pages/AdminVoice";
import Shop from "@/pages/Shop";
import Need from "@/pages/Need";
import CosmicBrain from "@/pages/CosmicBrain";
import LeadDeal from "@/pages/LeadDeal";
import SosAck from "@/pages/SosAck";
import AuthCallback from "@/pages/AuthCallback";
import About from "@/pages/About";
import MetalPocket from "@/pages/MetalPocket";
import Prices from "@/pages/Prices";
import Dealers from "@/pages/Dealers";
import OpsDashboard from "@/pages/OpsDashboard";
import ListMyBusiness from "@/pages/ListMyBusiness";
import Hub from "@/pages/Hub";
import Promote from "@/pages/Promote";
import MarketingKit from "@/pages/MarketingKit";
import PublicCard from "@/pages/PublicCard";
import { DonateThanks, DonateLeaderboard } from "@/pages/Donate";
import Learn from "@/pages/Learn";
import Parcels, { ParcelTracking } from "@/pages/Parcels";
import ERP from "@/pages/ERP";
import AppLauncher from "@/pages/AppLauncher";
import StorageSettings from "@/pages/StorageSettings";
import OfflineMaps from "@/pages/OfflineMaps";
import AiCompare from "@/pages/AiCompare";
import Dating from "@/pages/Dating";
import LeadsNew from "@/pages/LeadsNew";
import Directory from "@/pages/Directory";
import SocialFeed from "@/pages/SocialFeed";
import Messenger from "@/pages/Messenger";
import Storefront from "@/pages/Storefront";
import Wishlist from "@/pages/Wishlist";
import ProfileWall from "@/pages/ProfileWall";
import LiveComplaint from "@/pages/LiveComplaint";
import School from "@/pages/School";
import Flights from "@/pages/Flights";
import Cart, { Orders } from "@/pages/Cart";
import Entertainment from "@/pages/Entertainment";
import MicroPay from "@/pages/MicroPay";
import Delivery from "@/pages/Delivery";
import TrackDelivery from "@/pages/TrackDelivery";
import SchoolTeacherQR from "@/pages/SchoolTeacherQR";
import AutoListing from "@/pages/AutoListing";
import Recruiter from "@/pages/Recruiter";
import Property from "@/pages/Property";
import PropertyBrowse from "@/pages/PropertyBrowse";
import FindPay from "@/pages/FindPay";
import RideShare from "@/pages/RideShare";
import SharedRide from "@/pages/SharedRide";
import { FindHub, MarketHub, ConnectHub, MeHub, Redirect } from "@/pages/PrimaryHubs";
import Analytics from "@/pages/Analytics";
import TwoFactor, { TwoFactorGate } from "@/pages/TwoFactor";
import NotificationCenter from "@/pages/NotificationCenter";
import MyDevices from "@/pages/MyDevices";
import BusinessHub, { BusinessDashboard } from "@/pages/BusinessHub";
import BusinessOnboardingWizard from "@/pages/BusinessOnboardingWizard";
import LiveWorld from "@/pages/LiveWorld";
import AuctionDetail from "@/pages/AuctionDetail";
import Watchlist from "@/pages/Watchlist";
import TxnOrderDetail from "@/pages/OrderDetail";
import TxnOrdersList from "@/pages/OrdersList";
import TxnInbox from "@/pages/InboxPage";
import Business360 from "@/pages/Business360";
import EFP from "@/pages/EFP";
import AinaStudio from "@/pages/AinaStudio";
import AinaCreator from "@/pages/AinaCreator";
import AdminModeration from "@/pages/AdminModeration";
import WishlistPage from "@/pages/WishlistPage";
import ConnectionsPage from "@/pages/ConnectionsPage";
import NearbyPage from "@/pages/NearbyPage";
import NotificationPreferences from "@/pages/NotificationPreferences";
import BusinessCatalogue from "@/pages/BusinessCatalogue";
import OrderDetail, { OrdersMine } from "@/pages/OrderTracker";
import BusinessOrdersConsole from "@/pages/BusinessOrdersConsole";
import MatrimonialHub, { MatrimonialProfile, MatrimonialInbox, MatrimonialProfileView, MatrimonialPreferences } from "@/pages/Matrimonial";
import SocialPosts from "@/pages/SocialPosts";
import Wuid from "@/pages/Wuid";
import WalletHoldings from "@/pages/WalletHoldings";
import CivicPortals from "@/pages/CivicPortals";
import Contacts from "@/pages/Contacts";
import Contact from "@/pages/Contact";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import FindAMT from "@/pages/FindAMT";
import Assets from "@/pages/Assets";
import Bills from "@/pages/Bills";
import Explore18 from "@/pages/Explore18";
import LifeDashboard from "@/pages/LifeDashboard";
import Community from "@/pages/Community";
import Brands from "@/pages/Brands";
import ClubMemberships from "@/pages/ClubMemberships";
import OnboardingAI from "@/pages/OnboardingAI";
import Files from "@/pages/Files";
import Lab from "@/pages/Lab";
import Visa from "@/pages/Visa";
import ShareRedirect from "@/pages/ShareRedirect";
import SmartDashboard from "@/pages/SmartDashboard";
import Gov from "@/pages/Gov";
import GovAdmin from "@/pages/GovAdmin";
import Notices from "@/pages/Notices";
import FuelStations from "@/pages/FuelStations";
import Camps from "@/pages/Camps";
import Software from "@/pages/Software";
import PriorityCard from "@/pages/PriorityCard";
import Legal from "@/pages/Legal";
import Laws from "@/pages/Laws";
import Faqs from "@/pages/Faqs";
import LegalTemplates from "@/pages/LegalTemplates";
import useCopyProtect from "@/hooks/useCopyProtect";
import useAiPrefetch from "@/hooks/useAiPrefetch";
import OfflineOverlay from "@/components/OfflineOverlay";
import Volunteer from "@/pages/Volunteer";
import Schools from "@/pages/Schools";
import Products from "@/pages/Products";
import OfficialsOnboarding from "@/pages/OfficialsOnboarding";
import Jobs from "@/pages/Jobs";
import CarServices from "@/pages/CarServices";
import Washrooms from "@/pages/Washrooms";
import Health from "@/pages/Health";
import NearMeet from "@/pages/NearMeet";
import NetWorth from "@/pages/NetWorth";
import IvrAutobot from "@/pages/IvrAutobot";
import GstFiling from "@/pages/GstFiling";
import VideoIntros from "@/pages/VideoIntros";
import Reels from "@/pages/Reels";
import NfcHub from "@/pages/NfcHub";
import TapCardPublic from "@/pages/TapCardPublic";
import TapWifi from "@/pages/TapWifi";
import EventPublic from "@/pages/EventPublic";
import MerchantPayouts from "@/pages/MerchantPayouts";
import Astrology from "@/pages/Astrology";
import AstrologyRoom from "@/pages/AstrologyRoom";
import ServicesVertical from "@/pages/ServicesVertical";
import Tasks from "@/pages/Tasks";
import Charity from "@/pages/Charity";
import SponsorsDashboard from "@/pages/SponsorsDashboard";
import LiveTrains from "@/pages/LiveTrains";
import TripShare from "@/pages/TripShare";
import DigiLocker from "@/pages/DigiLocker";
import LockerShare from "@/pages/LockerShare";
import BasketShare from "@/pages/BasketShare";
import NearbyMap from "@/pages/NearbyMap";
import InstallPrompt from "@/components/InstallPrompt";
import AiAssistant from "@/components/AiAssistant";
import FeedbackButton from "@/components/FeedbackButton";

function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();
  useCopyProtect();     // deterrents + clipboard watermark, site-wide
  useAiPrefetch();      // AI-predicted background prefetch, per route change
  useGlobalPush(user?.user_id);   // Slice I⁺⁺⁺⁺⁺⁺ — global WS toast listener
  // Handle OAuth callback synchronously before other routes to prevent race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Layout>
      <LocationBootstrap />
      <LanguageAutoDetect />
      <NativeVoicePrompt />
      <SyncManager />
      <OfflineBanner />
      {user && <GlobalRideCallBanner />}
      <TwoFactorInterstitial>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/marketplace/:project_id" element={<Marketplace />} />
        <Route path="/marketplace/paid" element={<Marketplace />} />
        <Route path="/admin/marketplace" element={<AdminMarketplace />} />
        <Route path="/admin/moderation" element={<AdminModeration />} />
        <Route path="/admin/places" element={<AdminPlaces />} />
        <Route path="/tools" element={<MyTools />} />
        <Route path="/calculator" element={<Calculator />} />
        <Route path="/hr" element={<HrDashboard />} />
        <Route path="/search" element={<Search />} />
        <Route path="/venues" element={<Venues />} />
        <Route path="/venue-owner" element={<VenueOwner />} />
        <Route path="/venue/:slug" element={<Venue />} />
        <Route path="/admin/venues/:slug/edit" element={<AdminVenueEditor />} />
        <Route path="/services" element={<Superapp />} />
        <Route path="/ride/:delivery_id" element={<RideTrack />} />
        <Route path="/split/:delivery_id" element={<RideSplit />} />
        <Route path="/provider/shift" element={<ProviderShift />} />
        <Route path="/settings/language" element={<LanguageSettings />} />
        <Route path="/settings/emergency" element={<EmergencyContacts />} />
        <Route path="/settings/call-id" element={<CallId />} />
        <Route path="/settings/devices" element={<DevicePair />} />
        <Route path="/pair" element={<DevicePair />} />
        <Route path="/call/:id" element={<CallResolve />} />
        <Route path="/learn-earn" element={<LearnEarn />} />
        <Route path="/places" element={<PlacesLive />} />
        <Route path="/listing/:id" element={<ListingDetail />} />
        <Route path="/track/:id" element={<TrackBooking />} />
        <Route path="/post" element={<PostListing />} />
        <Route path="/edit/:listing_id" element={<PostListing />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/account" element={<Account />} />
        <Route path="/citizens" element={<Citizens />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/alerts" element={<Navigate to="/messenger" replace />} />
        <Route path="/emergency" element={<Emergency />} />
        <Route path="/sos/lift" element={<LiftSOS />} />
        <Route path="/ambulance" element={<AmbulancePage />} />
        <Route path="/admin/sos" element={<AdminSos />} />
        <Route path="/admin/ai" element={<AdminAi />} />
        <Route path="/admin/voice" element={<AdminVoice />} />
        <Route path="/cosmic" element={<CosmicBrain />} />
        <Route path="/leads/:leadId/deal" element={<LeadDeal />} />
        <Route path="/cosmic/:tool" element={<CosmicBrain />} />
        <Route path="/sos/ack/:id" element={<SosAck />} />
        {/* Legacy chat routes — canonical is /messenger (redirect below at line ~310) */}
        <Route path="/messages/:id" element={<ThreadRedirect />} />
        <Route path="/about" element={<About />} />
        <Route path="/metal-pocket" element={<MetalPocket />} />
        <Route path="/prices" element={<Prices />} />
        <Route path="/dealers" element={<Dealers />} />
        <Route path="/ops" element={<OpsDashboard />} />
        <Route path="/list-my-business" element={<ListMyBusiness />} />
        <Route path="/hub" element={<Hub />} />
        <Route path="/wallet" element={<Hub />} />
        <Route path="/promote" element={<Promote />} />
        <Route path="/marketing-kit" element={<MarketingKit />} />
        <Route path="/wallet/success" element={<Hub />} />
        <Route path="/card/:key" element={<PublicCard />} />
        <Route path="/welfare" element={<Navigate to="/charity" replace />} />
        <Route path="/donate" element={<Navigate to="/charity" replace />} />
        <Route path="/donate/campaign/:campaignId" element={<Navigate to="/charity" replace />} />
        <Route path="/donate/thanks" element={<DonateThanks />} />
        <Route path="/donate/leaderboard" element={<DonateLeaderboard />} />
        <Route path="/learn" element={<Learn />} />
        <Route path="/parcels" element={<Parcels />} />
        <Route path="/parcels/track/:trackingId" element={<ParcelTracking />} />
        <Route path="/erp" element={<ERP />} />
        <Route path="/apps" element={<AppLauncher />} />
        <Route path="/inbox" element={<Navigate to="/messenger" replace />} />
        <Route path="/ai/compare" element={<AiCompare />} />
        <Route path="/dating" element={<Dating />} />
        {/* Category convenience URLs — bounce to Search with the correct filter preset.
            Search page already exposes the "Verified creator" toggle (data-testid
            filter-verified-creator) so buyers can filter one tap earlier. */}
        <Route path="/matrimonial" element={<MatrimonialHub />} />
        <Route path="/matrimonial/profile" element={<MatrimonialProfile />} />
        <Route path="/matrimonial/preferences" element={<MatrimonialPreferences />} />
        <Route path="/matrimonial/inbox" element={<MatrimonialInbox />} />
        <Route path="/matrimonial/p/:profile_id" element={<MatrimonialProfileView />} />
        <Route path="/food" element={<Navigate to="/search?category=food" replace />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/need" element={<Need />} />
        <Route path="/leads/new" element={<LeadsNew />} />
        <Route path="/directory" element={<Directory />} />
        <Route path="/feed" element={<SocialFeed />} />
        <Route path="/messenger" element={<Messenger />} />
        <Route path="/messenger/:threadId" element={<Messenger />} />
        <Route path="/store/:key" element={<Storefront />} />
        <Route path="/u/:key" element={<ProfileWall />} />
        <Route path="/wishlist" element={<Wishlist />} />
        <Route path="/saved"    element={<Wishlist />} />
        <Route path="/complaint" element={<LiveComplaint />} />
        <Route path="/school" element={<School />} />
        <Route path="/flights" element={<Flights />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/entertainment" element={<Entertainment />} />
        <Route path="/micropay" element={<MicroPay />} />
        <Route path="/delivery" element={<Delivery />} />
        <Route path="/delivery/:id" element={<Delivery />} />
        <Route path="/track/delivery/:id" element={<TrackDelivery />} />
        <Route path="/school/teacher" element={<SchoolTeacherQR />} />
        <Route path="/autolist" element={<AutoListing />} />
        <Route path="/recruiter" element={<Recruiter />} />
        <Route path="/property" element={<Property />} />
        <Route path="/property/browse" element={<PropertyBrowse />} />
        <Route path="/findpay" element={<FindPay />} />
        <Route path="/rideshare" element={<RideShare />} />
        <Route path="/carpool"   element={<Navigate to="/rideshare" replace />} />
        <Route path="/r/:token"  element={<SharedRide />} />

        {/* P1a · Section 6 primary hubs (Home stays "/"). */}
        <Route path="/find"    element={<FindHub />} />
        <Route path="/market"  element={<MarketHub />} />
        <Route path="/connect" element={<ConnectHub />} />
        {/* Legacy redirect aliases — bookmarks + Google links must keep working. */}
        <Route path="/nearme"       element={<Redirect to="/map" />} />
        <Route path="/near-me"      element={<Redirect to="/map" />} />
        <Route path="/messages"     element={<Redirect to="/messenger" />} />
        <Route path="/pay"          element={<Redirect to="/findpay" />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/security" element={<TwoFactor />} />
        <Route path="/wuid" element={<Wuid />} />
        <Route path="/wallet/gold-silver" element={<WalletHoldings />} />
        <Route path="/civic-portals" element={<CivicPortals />} />
        <Route path="/contacts" element={<Contacts />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/findamt" element={<FindAMT />} />
        <Route path="/assets" element={<Assets />} />
        <Route path="/bills" element={<Bills />} />
        <Route path="/explore-18" element={<Explore18 />} />
        <Route path="/life" element={<LifeDashboard />} />
        <Route path="/community" element={<Community />} />
        <Route path="/pay-from-heart" element={<Community />} />
        <Route path="/brands" element={<Brands />} />
        <Route path="/clubs" element={<ClubMemberships />} />
        <Route path="/onboarding-ai" element={<OnboardingAI />} />
        <Route path="/files" element={<Files />} />
        <Route path="/f/:shareId" element={<ShareRedirect />} />
        <Route path="/lab" element={<Lab />} />
        <Route path="/foundation" element={<Navigate to="/charity" replace />} />
        <Route path="/visa" element={<Visa />} />
        <Route path="/visa/:code" element={<Visa />} />
        <Route path="/dashboard-smart" element={<SmartDashboard />} />
        <Route path="/me" element={<SmartDashboard />} />
        <Route path="/me/notifications" element={<NotificationCenter />} />
        <Route path="/me/security/devices" element={<MyDevices />} />
        <Route path="/notifications" element={<Navigate to="/me/notifications" replace />} />
        <Route path="/business" element={<BusinessHub />} />
        <Route path="/business/:id" element={<BusinessDashboard />} />
        <Route path="/business/setup" element={<BusinessOnboardingWizard />} />
        <Route path="/live-world" element={<LiveWorld />} />
        <Route path="/live-world/watchlist" element={<Watchlist />} />
        <Route path="/live-world/auction/:id" element={<AuctionDetail />} />
        <Route path="/liveworld" element={<Navigate to="/live-world" replace />} />
        <Route path="/transactions/orders" element={<TxnOrdersList />} />
        <Route path="/transactions/order/:id" element={<TxnOrderDetail />} />
        <Route path="/transactions/inbox" element={<TxnInbox />} />
        <Route path="/wishlist" element={<WishlistPage />} />
        <Route path="/social/connections" element={<ConnectionsPage />} />
        <Route path="/live-world/nearby" element={<NearbyPage />} />
        <Route path="/business-360" element={<Business360 />} />
        <Route path="/efp" element={<EFP />} />
        <Route path="/aina/studio" element={<AinaStudio />} />
        <Route path="/aina/creator" element={<AinaCreator />} />
        <Route path="/settings/notifications" element={<NotificationPreferences />} />
        <Route path="/business/onboarding" element={<Navigate to="/business/setup" replace />} />
        <Route path="/business/:id/catalogue" element={<BusinessCatalogue />} />
        <Route path="/business/:id/orders" element={<BusinessOrdersConsole />} />
        <Route path="/orders/mine" element={<OrdersMine />} />
        <Route path="/orders/:order_id" element={<OrderDetail />} />
        <Route path="/social" element={<SocialPosts />} />
        <Route path="/gov" element={<Gov />} />
        <Route path="/notices" element={<Notices />} />
        <Route path="/fuel" element={<FuelStations />} />
        <Route path="/camps" element={<Camps />} />
        <Route path="/software" element={<Software />} />
        <Route path="/priority-card" element={<PriorityCard />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/laws"  element={<Laws />} />
        <Route path="/faqs"  element={<Faqs />} />
        <Route path="/legal-templates" element={<LegalTemplates />} />
        <Route path="/gov/admin" element={<GovAdmin />} />
        <Route path="/volunteer" element={<Volunteer />} />
        <Route path="/city-builder" element={<Volunteer />} />
        <Route path="/schools" element={<Schools />} />
        <Route path="/products" element={<Products />} />
        <Route path="/officials/onboard" element={<OfficialsOnboarding />} />
        <Route path="/jobs" element={<Jobs />} />
        <Route path="/car-services" element={<CarServices />} />
        <Route path="/washrooms" element={<Washrooms />} />
        <Route path="/health" element={<Health />} />
        <Route path="/nearmeet" element={<NearMeet />} />
        <Route path="/net-worth" element={<NetWorth />} />
        <Route path="/ivr" element={<IvrAutobot />} />
        <Route path="/gst-filing" element={<GstFiling />} />
        <Route path="/video-intros" element={<VideoIntros />} />
        <Route path="/reels" element={<Reels />} />
        <Route path="/nfc" element={<NfcHub />} />
        <Route path="/c/:slug" element={<TapCardPublic />} />
        <Route path="/wifi/:tag_id" element={<TapWifi />} />
        <Route path="/e/:slug" element={<EventPublic />} />
        <Route path="/merchant/payouts" element={<MerchantPayouts />} />
        <Route path="/astrology" element={<Astrology />} />
        <Route path="/astrology/room/:booking_id" element={<AstrologyRoom />} />
        <Route path="/services/:vertical" element={<ServicesVertical />} />
        <Route path="/tasks" element={<Tasks />} />
        <Route path="/charity" element={<Charity />} />
        <Route path="/sponsors/dashboard" element={<SponsorsDashboard />} />
        <Route path="/live-trains" element={<LiveTrains />} />
        <Route path="/trip/:shareId" element={<TripShare />} />
        <Route path="/journey" element={<Navigate to="/live-trains" replace />} />
        <Route path="/locker" element={<DigiLocker />} />
        <Route path="/locker/share/:shareId" element={<LockerShare />} />
        <Route path="/basket/:code" element={<BasketShare />} />
        {/* Nearby / Personal Map — canonical route + aliases (Bug fix C1/H5) */}
        <Route path="/nearby"    element={<NearbyMap />} />
        <Route path="/settings/storage" element={<StorageSettings />} />
        <Route path="/settings/offline" element={<OfflineMaps />} />
        <Route path="/map"       element={<NearbyMap />} />
        <Route path="/discover"  element={<NearbyMap />} />
        {/* External URL aliases (Bug fix H5) — direct browser navigation + refresh */}
        <Route path="/listings"  element={<Navigate to="/directory" replace />} />
        <Route path="/movies"    element={<Navigate to="/entertainment" replace />} />
        <Route path="/meet"      element={<NearMeet />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/ott" element={<Ott />} />
        <Route path="/ott/watch/:id" element={<OttWatch />} />
        <Route path="/ott/upload" element={<OttUpload />} />
        <Route path="/ott/creator/:id" element={<OttCreator />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </TwoFactorInterstitial>
    </Layout>
  );
}

// Blocks the app UI when the logged-in user has TOTP enabled but this session
// hasn't cleared the 2FA challenge. Allowed routes (auth callback, the security
// page itself) render normally so the user can always sign out or manage 2FA.
function TwoFactorInterstitial({ children }) {
  const { user, checkAuth } = useAuth();
  const location = useLocation();
  const allowlist = ["/security", "/auth/callback"];
  if (!user || !user.requires_2fa) return children;
  if (allowlist.some((p) => location.pathname.startsWith(p))) return children;
  return <TwoFactorGate onVerified={checkAuth} />;
}

function ThreadRedirect() {
  const { id } = useParams();
  return <Navigate to={`/messenger/${id}`} replace />;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <LocationProvider>
          <I18nProvider>
            <BrowserRouter>
              <AppRouter />
              <InstallPrompt />
              <AiAssistant />
              <FeedbackButton />
              <Toaster position="top-center" richColors offset={80} />
              <StepUpDialog />
            </BrowserRouter>
          </I18nProvider>
        </LocationProvider>
      </AuthProvider>
    </div>
  );
}
